import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../api';
import { resolveImageUrl } from '../utils/imageUrl';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import Breadcrumb from '../components/Breadcrumb';
import { formatDate } from '../utils/dateUtils';
import { useToast } from '../components/ToastProvider';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { openOrDownloadPdf } from '../utils/generatePdf';
import { pageContainerSx } from '../styles/tokens';

const DeliveryChecklistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const bookingsReturnPath = location.state?.fromBookings || '/bookings';
  const bookingState = { fromBookings: bookingsReturnPath };

  const goToBookings = () => navigate(bookingsReturnPath);
  const goToBookingDetails = (bookingId) => navigate(`/bookings/${bookingId}`, { state: bookingState });

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  // checkState[product_id] = { dispatched: bool, received: bool }
  const [checkState, setCheckState] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `/api/bookings/${id}/delivery-checklist`
        );
        setData(res.data);
        const items = res.data?.items || [];
        const initial = {};
        items.forEach((it) => {
          initial[it.product_id] = { dispatched: false, received: false };
        });
        setCheckState(initial);
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          `Failed to load delivery checklist (${err.response?.status || 'network error'})`;
        console.error('Delivery checklist load error', err);
        showToast(msg, 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const openChecklistPdf = async () => {
    if (!id) return;
    try {
      const items = data?.items || [];
      const body = {
        items: items.map((it) => ({
          product_id: it.product_id,
          dispatched: checkState[it.product_id]?.dispatched ?? false,
          received: checkState[it.product_id]?.received ?? false,
        })),
      };
      const res = await axios.post(
        `/api/bookings/${id}/delivery-checklist-pdf`,
        body,
        { responseType: 'blob', headers: { 'Content-Type': 'application/json' } }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      openOrDownloadPdf(blob, `delivery-checklist-${id}.pdf`);
    } catch (error) {
      showToast(
        error.response?.data?.message || 'Failed to generate Delivery Checklist PDF',
        'error'
      );
    }
  };

  const setItemChecked = (productId, field, checked) => {
    setCheckState((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [field]: checked },
    }));
  };

  if (loading) {
    return <PageLoader message="Loading delivery checklist..." />;
  }

  if (!data) {
    return (
      <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography color="text.secondary">Delivery checklist not found.</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goToBookings}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          Back to Bookings
        </Button>
      </Box>
    );
  }

  const items = data.items || [];

  return (
    <Box sx={{ ...pageContainerSx, maxWidth: 1100, px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <Breadcrumb
        items={[
          { label: 'Bookings', path: bookingsReturnPath },
          { label: `Order #${data.booking_id}`, path: `/bookings/${data.booking_id}`, state: bookingState },
          { label: 'Delivery Checklist', path: null },
        ]}
      />

      <PageHeader
        title="Delivery Checklist"
        subtitle={`Order #${data.booking_id} · ${data.customer_name || 'Customer'}`}
        actions={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
              onClick={() => goToBookingDetails(data.booking_id)}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', borderRadius: 999, height: 40, px: 2 }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
              onClick={openChecklistPdf}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', borderRadius: 999, height: 40, px: 2.5 }}
            >
              Export PDF
            </Button>
          </Box>
        )}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >

        <Box sx={{ p: { xs: 2, sm: 3 }, '& > * + *': { mt: { xs: 2.5, sm: 3 } } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' },
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Customer
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                {data.customer_name}
              </Typography>
              {data.company_name && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {data.company_name}
                </Typography>
              )}
              {data.place && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                  {data.place}
                </Typography>
              )}
              {data.primary_phone && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                  {data.primary_phone}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: { xs: 1.25, sm: 1.75 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  Dispatch Date
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {formatDate(data.dispatch_date)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  Return Date
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {formatDate(data.return_date) || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
                mb: 1,
              }}
            >
              Items
            </Typography>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {items.map((item, index) => {
                  const imgSrc = resolveImageUrl(item.image_url);
                  return (
                    <Paper
                      key={`${item.product_id}-${index}`}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 auto', minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 20 }}>{index + 1}</Typography>
                        {imgSrc ? (
                          <Box
                            component="img"
                            src={imgSrc}
                            alt={item.name}
                            sx={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
                            onClick={() => setZoomImage(imgSrc)}
                          />
                        ) : (
                          <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>No img</Typography>
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{item.name}</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{item.category} · {item.product_code} · Qty {item.quantity}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Dispatched</Typography>
                          <Checkbox
                            size="small"
                            checked={checkState[item.product_id]?.dispatched ?? false}
                            onChange={(e) => setItemChecked(item.product_id, 'dispatched', e.target.checked)}
                            sx={{ py: 0 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Received</Typography>
                          <Checkbox
                            size="small"
                            checked={checkState[item.product_id]?.received ?? false}
                            onChange={(e) => setItemChecked(item.product_id, 'received', e.target.checked)}
                            sx={{ py: 0 }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Sl</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Image</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Category</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Description</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Code</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Qty</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Dispatched</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>Received</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const imgSrc = resolveImageUrl(item.image_url);
                      return (
                        <TableRow key={`${item.product_id}-${index}`}>
                          <TableCell sx={{ fontSize: 12 }}>{index + 1}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {imgSrc ? (
                              <Box component="img" src={imgSrc} alt={item.name} sx={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 0.5, cursor: 'pointer', border: '1px solid', borderColor: 'divider' }} onClick={() => setZoomImage(imgSrc)} />
                            ) : (
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>No image</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{item.category}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{item.product_code}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{item.quantity}</TableCell>
                          <TableCell sx={{ fontSize: 12 }} padding="checkbox">
                            <Checkbox size="small" checked={checkState[item.product_id]?.dispatched ?? false} onChange={(e) => setItemChecked(item.product_id, 'dispatched', e.target.checked)} sx={{ py: 0 }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }} padding="checkbox">
                            <Checkbox size="small" checked={checkState[item.product_id]?.received ?? false} onChange={(e) => setItemChecked(item.product_id, 'received', e.target.checked)} sx={{ py: 0 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={Boolean(zoomImage)}
        onClose={() => setZoomImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            bgcolor: 'rgba(0,0,0,0.25)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <IconButton
              onClick={() => setZoomImage(null)}
              size="small"
              aria-label="Close"
              sx={{
                position: 'absolute',
                top: { xs: 10, sm: 14 },
                right: { xs: 10, sm: 14 },
                bgcolor: 'rgba(255,255,255,0.9)',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {zoomImage && (
              <Box
                component="img"
                src={zoomImage}
                alt="Product"
                sx={{
                  maxHeight: '80vh',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeliveryChecklistPage;

